import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import Breadcrumb from '../../../../core/common/Breadcrumb/breadcrumb';
import Sidebar from '../../sidebar/sidebar';

type Servico = {
  id: number;
  nome: string;
  icone?: string; // opcional se backend retornar
};

type EmpresaData = {
  nome: string;
  nif: string;
  email: string;
  telefone: string;
  rua: string;
  cidade: string;
  codigoPostal: string;
  pais: string;
  descricao: string;
  nib: string;
  numeroCertidaoPermanente: string;
  servicos: Servico[];
  status?: string; // APROVADA, REJEITADA, PENDENTE
  motivoRejeicao?: string;
};


type User = {
  id: number;
  name: string;
  email: string;
  role: string;
};

const AgentAccountSettings = () => {
  const routes = all_routes;

  const [empresa, setEmpresa] = useState<EmpresaData>({
    nome: '',
    nif: '',
    email: '',
    telefone: '',
    rua: '',
    cidade: '',
    codigoPostal: '',
    pais: '',
    descricao: '',
    nib: '',
    numeroCertidaoPermanente: '',
    servicos: [],
  });

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_URL;

  const token = localStorage.getItem('token');

  // Fetch company data

  const [servicosDisponiveis, setServicosDisponiveis] = useState<Servico[]>([]);
  const [servicosSelecionados, setServicosSelecionados] = useState<number[]>([]);


  useEffect(() => {
    const fetchServicos = async () => {
      try {
        const res = await fetch(`${BASE_URL}/servicos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setServicosDisponiveis(data);
      } catch (err) {
        console.error("Erro ao buscar serviços", err);
      }
    };
    if (token) fetchServicos();
  }, [token]);


  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const response = await fetch(`${BASE_URL}/empresas/minha`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        setEmpresa({
          nome: data.nome || '',
          nif: data.nif || '',
          email: data.email || '',
          telefone: data.telefone || '',
          rua: data.rua || '',
          cidade: data.cidade || '',
          codigoPostal: data.codigoPostal || '',
          pais: data.pais || '',
          descricao: data.descricao || '',
          nib: data.nib || '',
          numeroCertidaoPermanente: data.numeroCertidaoPermanente || '',
          servicos: data.servicos || [],
          status: data.status || 'PENDENTE',
          motivoRejeicao: data.motivoRejeicao || '',
        });
        setServicosSelecionados(data.servicos?.map((s: Servico) => s.id) || []);

      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmpresa();
  }, [token]);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BASE_URL}/usuarios/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setUser(data);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (token) {
      fetchUser();
    }
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmpresa((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const empresaParaEnviar = {
        ...empresa,
        servicosIds: servicosSelecionados,
      };

      await fetch(`${BASE_URL}/empresas/minha`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(empresaParaEnviar),
      });
      alert('Company updated successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error updating company:', error);
      alert('Failed to update company.');
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: 'Dashboard', link: routes.agentDashboard },
    { label: 'Company Info', active: true },
  ];

  const handleCheckboxChange = (id: number) => {
    setServicosSelecionados(prev => {
      const jaSelecionado = prev.includes(id);
      if (jaSelecionado) {
        return prev.filter(sid => sid !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Coloque isso dentro do componente AgentAccountSettings, antes do return:

  const camposTexto: (keyof Omit<EmpresaData, 'servicos' | 'descricao' | 'status' | 'motivoRejeicao'>)[] = [
    'nome',
    'nif',
    'email',
    'telefone',
    'rua',
    'cidade',
    'codigoPostal',
    'pais',
    'nib',
    'numeroCertidaoPermanente',
  ];

  const labelsMap: Record<typeof camposTexto[number], string> = {
    nome: 'Company Name',
    nif: 'NIF',
    email: 'Email',
    telefone: 'Phone',
    rua: 'Street',
    cidade: 'City',
    codigoPostal: 'Postal Code',
    pais: 'Country',
    nib: 'NIB',
    numeroCertidaoPermanente: 'Permanent Certificate Number',
  };

  const reenviarParaRevisao = async () => {
    try {
      await fetch(`${BASE_URL}/empresas/reenviar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      alert("Empresa reenviada para revisão!");
      window.location.reload();
    } catch (err) {
      console.error("Erro ao reenviar empresa", err);
      alert("Erro ao reenviar para revisão.");
    }
  };

  return (
    <div>
      <Breadcrumb title="My Company" breadcrumbs={breadcrumbs} backgroundClass="breadcrumb-bg-04" />
      <div className="content">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-xl-3 col-lg-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-xl-9 col-lg-8">
              <div className="card settings mb-0">
                <div className="card-header">
                  <h6>Company Details</h6>
                </div>
                <div className="card-body pb-3">
                  {/* Settings navigation */}
                  <div className="settings-link d-flex align-items-center flex-wrap mb-4">
                    <Link to={routes.agentSettings} className="ps-3">
                      <i className="isax isax-user-octagon me-2" /> Edit Profile
                    </Link>
                    <Link to={routes.agentAccountSettings} className="active">
                      <i className="isax isax-wallet-money me-2" /> Company Info
                    </Link>
                    <Link to={routes.agentContract}>
                      <i className="isax isax-document-text me-2" /> Contract
                    </Link>
                    <Link to={routes.agentSecuritySettings}>
                      <i className="isax isax-shield-tick me-2" /> Security
                    </Link>
                    <Link to={routes.agentPlanSettings}>
                      <i className="isax isax-star me-2" /> Plans & Billing
                    </Link>
                  </div>

                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <>
                      {/* Status da empresa */}
                      <div className="mb-4 p-3 border rounded bg-light">
                        <strong>Status da empresa: </strong>
                        <span
                          className={
                            empresa.status === "APROVADA"
                              ? "text-success"
                              : empresa.status === "REJEITADA"
                                ? "text-danger"
                                : "text-warning"
                          }
                        >
                          {empresa.status}
                        </span>

                        {empresa.status === "REJEITADA" && empresa.motivoRejeicao && (
                          <div className="mt-2">
                            <strong>Motivo da rejeição:</strong>
                            <p className="mb-2">{empresa.motivoRejeicao}</p>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={reenviarParaRevisao}
                            >
                              Reenviar para revisão
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Formulário de edição */}
                      <form>
                        <div className="row">
                          {camposTexto.map((key) => (
                            <div className="col-md-6" key={key}>
                              <div className="mb-3">
                                <label className="form-label">{labelsMap[key]}</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name={key}
                                  value={empresa[key]}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                          ))}

                          <div className="col-12">
                            <div className="mb-3">
                              <label className="form-label">Description</label>
                              <textarea
                                className="form-control"
                                name="descricao"
                                rows={4}
                                value={empresa.descricao}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="mb-3">
                            <label className="form-label">Services Provided</label>
                            <div className="form-check d-flex flex-wrap gap-3">
                              {servicosDisponiveis.map(servico => (
                                <div key={servico.id} className="form-check form-check-inline">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`servico-${servico.id}`}
                                    checked={servicosSelecionados.includes(servico.id)}
                                    onChange={() => handleCheckboxChange(servico.id)}
                                  />
                                  <label htmlFor={`servico-${servico.id}`} className="form-check-label">
                                    {servico.icone && <span>{servico.icone} </span>}
                                    {servico.nome}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* /Main Content */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentAccountSettings;
